import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Search, Loader2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useSearch } from '@/lib/hooks/useSearch';
import { useDebouncedValue } from '@/lib/hooks';
import { SearchObjectCard } from './SearchObjectCard';
import { getSearchObjectUrl } from '@/lib/utils/search-object-urls';
interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export function SearchDialog(t0) {
  const $ = _c(54);
  const {
    open,
    onOpenChange
  } = t0;
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [viewportHeight, setViewportHeight] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [shortcutKey, setShortcutKey] = useState("Ctrl");
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const router = useRouter();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      setIsMobile(isMobileDevice);
      const isMac = /mac|iphone|ipad|ipod/.test(userAgent);
      setShortcutKey(isMac ? "Cmd" : "Ctrl");
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const debouncedQuery = useDebouncedValue(query, 300);
  let t3;
  let t4;
  if ($[2] !== open) {
    t3 = () => {
      if (!open) {
        return;
      }
      const updateHeight = () => {
        setViewportHeight(window.visualViewport?.height || window.innerHeight);
      };
      updateHeight();
      window.visualViewport?.addEventListener("resize", updateHeight);
      window.addEventListener("resize", updateHeight);
      return () => {
        window.visualViewport?.removeEventListener("resize", updateHeight);
        window.removeEventListener("resize", updateHeight);
      };
    };
    t4 = [open];
    $[2] = open;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== open) {
    t5 = () => {
      if (open) {
        setSelectedIndex(-1);
        if (inputRef.current) {
          setTimeout(() => {
            inputRef.current?.focus();
          }, 100);
        }
      }
    };
    t6 = [open];
    $[5] = open;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setSelectedIndex(-1);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== debouncedQuery) {
    t8 = [debouncedQuery];
    $[9] = debouncedQuery;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  useEffect(t7, t8);
  let t9;
  if ($[11] !== debouncedQuery) {
    t9 = debouncedQuery.trim();
    $[11] = debouncedQuery;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = t9.length > 0;
  let t11;
  if ($[13] !== debouncedQuery || $[14] !== t10) {
    t11 = {
      query: debouncedQuery,
      limit: 10,
      enabled: t10
    };
    $[13] = debouncedQuery;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const {
    data,
    isLoading,
    error
  } = useSearch(t11);
  const results = data?.pages?.[0]?.results || [];
  let t12;
  let t13;
  if ($[16] !== selectedIndex) {
    t12 = () => {
      if (selectedIndex >= 0 && resultsRef.current) {
        const selectedElement = resultsRef.current.querySelector(`[data-result-index="${selectedIndex}"]`);
        if (selectedElement) {
          selectedElement.scrollIntoView({
            block: "nearest",
            behavior: "smooth"
          });
        }
      }
    };
    t13 = [selectedIndex];
    $[16] = selectedIndex;
    $[17] = t12;
    $[18] = t13;
  } else {
    t12 = $[17];
    t13 = $[18];
  }
  useEffect(t12, t13);
  const handleKeyDown = e => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => prev < results.length ? prev + 1 : prev);
    } else {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(_temp);
      } else {
        if (e.key === "Enter") {
          e.preventDefault();
          if (selectedIndex === results.length) {
            handleNavigateToSearch();
          } else {
            if (selectedIndex >= 0 && selectedIndex < results.length) {
              const selectedResult = results[selectedIndex];
              handleResultNavigation(selectedResult);
            } else {
              if (query.trim()) {
                handleNavigateToSearch();
              }
            }
          }
        } else {
          if (e.key === "Escape") {
            onOpenChange(false);
          }
        }
      }
    }
  };
  const handleResultNavigation = result => {
    onOpenChange(false);
    router.push(getSearchObjectUrl(result));
  };
  const handleNavigateToSearch = () => {
    onOpenChange(false);
    router.push(`/search?q=${encodeURIComponent(query)}`);
  };
  const handleResultClick = result_0 => {
    handleResultNavigation(result_0);
  };
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setQuery("");
      inputRef.current?.focus();
    };
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const handleClearQuery = t14;
  const t15 = viewportHeight ? `${Math.min(viewportHeight * 0.9, viewportHeight - 16)}px` : "min(90dvh, calc(var(--app-viewport-height, 100dvh) - 1rem))";
  let t16;
  if ($[20] !== t15) {
    t16 = {
      height: t15
    };
    $[20] = t15;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <DialogHeader className="px-6 py-4 border-b border-gray-100 flex-shrink-0"><DialogTitle className="text-lg font-semibold">Search</DialogTitle></DialogHeader>;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== isMobile || $[24] !== shortcutKey) {
    t18 = !isMobile && <p className="text-xs text-gray-500 mb-2">Press{" "}<kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs font-mono">{shortcutKey}+/</kbd>{" "}to open search anytime</p>;
    $[23] = isMobile;
    $[24] = shortcutKey;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = e_0 => setQuery(e_0.target.value);
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== query) {
    t21 = query && <button onClick={handleClearQuery} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer" aria-label="Clear search"><X className="w-5 h-5" /></button>;
    $[28] = query;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== isLoading || $[31] !== query) {
    t22 = isLoading && !query && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 animate-spin" />;
    $[30] = isLoading;
    $[31] = query;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  const t23 = "flex-1 min-h-0 overflow-y-auto";
  let t24;
  if ($[33] !== isLoading) {
    t24 = isLoading && <div className="p-8 text-center text-gray-500"><Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" /><p className="text-sm">Searching...</p></div>;
    $[33] = isLoading;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== error) {
    t25 = error && <div className="p-8 text-center text-red-600"><p className="text-sm">Failed to load search results. Please try again.</p></div>;
    $[35] = error;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  const t26 = !isLoading && !error && debouncedQuery && results.length === 0 && <div className="p-8 text-center text-gray-500"><p className="text-sm">No results found for "{debouncedQuery}"</p></div>;
  let t27;
  if ($[37] !== debouncedQuery || $[38] !== error || $[39] !== isLoading) {
    t27 = !isLoading && !error && !debouncedQuery && <div className="p-8 text-center text-gray-500"><p className="text-sm">Start typing to search Sizzle</p></div>;
    $[37] = debouncedQuery;
    $[38] = error;
    $[39] = isLoading;
    $[40] = t27;
  } else {
    t27 = $[40];
  }
  const t28 = !isLoading && !error && results.length > 0 && <div className="divide-y divide-gray-100">{results.map((result_1, index) => <div key={`${result_1.objectType}-${result_1.objectId}`} data-result-index={index} onClick={() => handleResultClick(result_1)} className={`cursor-pointer transition-colors ${selectedIndex === index ? "bg-primary-50" : "hover:bg-gray-50"}`}><SearchObjectCard result={result_1} variant="compact" /></div>)}</div>;
  let t29;
  if ($[41] !== t24 || $[42] !== t25 || $[43] !== t26 || $[44] !== t27 || $[45] !== t28) {
    t29 = <div ref={resultsRef} className={t23}>{t24}{t25}{t26}{t27}{t28}</div>;
    $[41] = t24;
    $[42] = t25;
    $[43] = t26;
    $[44] = t27;
    $[45] = t28;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] !== error || $[48] !== handleNavigateToSearch || $[49] !== isLoading || $[50] !== query || $[51] !== results || $[52] !== selectedIndex) {
    t30 = !isLoading && !error && results.length > 0 && <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0"><button onClick={handleNavigateToSearch} className={`w-full py-2.5 text-sm text-primary-600 font-medium rounded-lg transition-colors cursor-pointer ${selectedIndex === results.length ? "bg-primary-100" : "hover:bg-primary-50"}`}>Search all results for "{query}"</button></div>;
    $[47] = error;
    $[48] = handleNavigateToSearch;
    $[49] = isLoading;
    $[50] = query;
    $[51] = results;
    $[52] = selectedIndex;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="p-0 max-w-2xl flex flex-col gap-0" style={t16}>{t17}<div className="px-6 py-4 border-b border-gray-100 flex-shrink-0">{t18}<div className="relative">{t19}<input ref={inputRef} type="text" value={query} onChange={t20} onKeyDown={handleKeyDown} placeholder="Search..." className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" aria-label="Search" role="searchbox" />{t21}{t22}</div></div>{t29}{t30}</DialogContent></Dialog>;
}
function _temp(prev_0) {
  return prev_0 > 0 ? prev_0 - 1 : -1;
}